import React, { createContext, useContext, useState } from 'react'
import './help.css'

type HelpContextType = {
  currentStep: number | null
  setCurrentStep: (step: number | null) => void
  currentHelpContext: string | null
  setCurrentHelpContext: (context: string | null) => void
  startHelp: (context: string) => void
  nextStep: () => void
  prevStep: () => void
  stopHelp: () => void
  anchorEl: HTMLElement | null
  setAnchorEl: (el: HTMLElement | null) => void
}

const HelpContext = createContext<HelpContextType | undefined>(undefined)

// export const HelpProvider = ({ children }: { children: React.ReactNode }) => {
//   const [currentStep, setCurrentStep] = useState<number | null>(null)
//   const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
//   // const [newDealEl, setNewDealEl] = useState<HTMLElement | null>(null)

//   const startHelp = () => setCurrentStep(0)
//   const nextStep = () => setCurrentStep(prev => (prev !== null ? prev + 1 : null))
//   const stopHelp = () => {
//     setCurrentStep(null)
//     setAnchorEl(null)
//   }
//   const prevStep = () => setCurrentStep(prev => (prev !== null && prev > 0 ? prev - 1 : prev))

//   return (
//     <HelpContext.Provider
//       value={{ currentStep, setCurrentStep, startHelp, nextStep, prevStep, stopHelp, anchorEl, setAnchorEl }}
//     >
//       {children}
//     </HelpContext.Provider>
//   )
// }
export const HelpProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentStep, setCurrentStep] = useState<number | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [currentHelpContext, setCurrentHelpContext] = useState<string | null>(null)

  const startHelp = (context: string) => {
    setCurrentHelpContext(context)
    setCurrentStep(0)
  }

  const nextStep = () => setCurrentStep(prev => (prev !== null ? prev + 1 : null))
  const stopHelp = () => {
    setCurrentStep(null)
    setCurrentHelpContext(null)
    setAnchorEl(null)
  }

  const prevStep = () => setCurrentStep(prev => (prev !== null && prev > 0 ? prev - 1 : prev))

  return (
    <HelpContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        currentHelpContext,
        setCurrentHelpContext,
        startHelp,
        nextStep,
        prevStep,
        stopHelp,
        anchorEl,
        setAnchorEl,
      }}
    >
      {children}
    </HelpContext.Provider>
  )
}

export const useHelp = () => {
  const context = useContext(HelpContext)
  if (!context) throw new Error('useHelp must be used within a HelpProvider')
  return context
}

/*
🔥 Right now, ChatGPT memory management is very simple (and a bit manual):
When you open Settings > Personalization > Manage Memory,
you'll see a list of memory "cards" — each memory has:

a short description like “User is working on a CRM project”

a date of when that memory was saved

an option to "Delete" individual ones.

BUT:

There's no automatic indicator like "outdated" or "no longer used."

You have to manually judge which ones feel outdated or not useful anymore.

🧠 Here's an efficient strategy I've seen work best:
Step 1: Sort memories into 3 buckets mentally
When you read each memory, ask:


Decision	If yes...	If no...
Still Active?	Keep it	Move on
Was this a Past Project or Situation?	Delete it if fully complete	
Still Matches Your Current Goals or Context?	Keep it	Delete if outdated
Step 2: Use easy markers like:

✅ Keep anything about your current main projects (ex: "Building project management dashboard")

✅ Keep anything about your preferences that haven't changed (ex: "Prefers clear, structured answers")

❌ Delete things tied to old, completed, abandoned, or irrelevant work (ex: "Working on onboarding v1.2" if you're now on v2.8)

❌ Delete test memories ("User tested setting up DynamoDB dev environment") unless still actively useful

❌ Delete one-off help topics you already mastered (like "learning basic TypeScript" if you're now advanced)

⚡ Ultra quick tip:
When you're skimming the memory list —
if you think "Oh, I forgot that was even a thing",
➡️ it's probably safe to delete.

Example:

Memory	Action
"User is developing CRM dashboard"	Keep
"User was troubleshooting Slack OAuth errors in Feb 2024"	Delete if fixed long ago
"User prefers concise bullet-point answers"	Keep
"User was learning React basics"	Delete if you're way past that now
📢 Important:
Deleting a memory does not delete your saved conversations — just the context I can automatically remember going forward.

You can always re-teach me something manually in a conversation later if you change your mind.

Would you like me to help you come up with a simple checklist you can use in your head while reviewing (like a 5-second per memory flow)?
It makes memory cleanup go way faster if you're doing a lot! 🚀
(Only if you want!)


*/
